import { request } from '@/common/services/request';
import type {
  InvitationInfo,
  NetworkBusiness,
  NetworkEligibility,
  NetworkOverview,
  NetworkReward,
  NetworkTeamScope,
  OffsetPage,
  TeamMember,
} from '@/types/api';

export type {
  InvitationInfo,
  NetworkBusiness,
  NetworkEligibility,
  NetworkOverview,
  NetworkReward,
  NetworkRewardStatus,
  NetworkRewardType,
  NetworkTeamScope,
  TeamMember,
} from '@/types/api';

/** GET /v1/network/invitation */
export const getInvitation = (): Promise<InvitationInfo> => {
  return request({
    url: '/v1/network/invitation',
    method: 'GET',
  });
};

/** POST /v1/network/bind — binding is permanent */
export const bindInvitation = (code: string): Promise<{ bound: boolean }> => {
  return request({
    url: '/v1/network/bind',
    method: 'POST',
    data: { code },
    // 登录/邀请弹窗已自行 toast
    showError: false,
  });
};

export interface NetworkQueryParams {
  /** 显式传入启用 11.5 分业务响应 */
  business?: NetworkBusiness;
  /** 查看直推的下级团队（两层浏览） */
  parentUserId?: string;
  scope?: NetworkTeamScope;
  eligibility?: NetworkEligibility;
  offset?: number;
  limit?: number;
}

/** GET /v1/network/overview — 可带 business / parentUserId */
export const getNetworkOverview = (
  params: Pick<NetworkQueryParams, 'business' | 'parentUserId'> = {}
): Promise<NetworkOverview> => {
  const data: Record<string, string> = {};
  if (params.business) data.business = params.business;
  if (params.parentUserId) data.parentUserId = params.parentUserId;
  return request({
    url: '/v1/network/overview',
    method: 'GET',
    data,
  });
};

/** GET /v1/network/team — limit max 100；显式 business 启用 11.5/11.6 */
export const getNetworkTeam = (
  params: NetworkQueryParams = {}
): Promise<OffsetPage<TeamMember>> => {
  const data: Record<string, string | number> = {
    offset: Math.max(0, Math.min(100000, params.offset ?? 0)),
    limit: Math.max(1, Math.min(100, params.limit ?? 50)),
  };
  if (params.business) data.business = params.business;
  if (params.parentUserId) data.parentUserId = params.parentUserId;
  if (params.scope) data.scope = params.scope;
  if (params.eligibility) data.eligibility = params.eligibility;
  return request({
    url: '/v1/network/team',
    method: 'GET',
    data,
  });
};

/** GET /v1/network/rewards — limit max 100 */
export const getNetworkRewards = (
  params: { offset?: number; limit?: number } = {}
): Promise<OffsetPage<NetworkReward>> => {
  return request({
    url: '/v1/network/rewards',
    method: 'GET',
    data: {
      offset: params.offset ?? 0,
      limit: params.limit ?? 50,
    },
  });
};

/** GET /v1/network/rewards/:id — §20.2 */
export const getNetworkReward = (
  id: string
): Promise<NetworkReward & Record<string, unknown>> => {
  return request({
    url: `/v1/network/rewards/${encodeURIComponent(id)}`,
    method: 'GET',
  });
};

/** PUT /v1/network/team/:memberUserId/note — 私人备注，跨设备同步 */
export const putTeamMemberNote = (
  memberUserId: string,
  note: string
): Promise<{ memberUserId: string; note: string }> => {
  return request({
    url: `/v1/network/team/${encodeURIComponent(memberUserId)}/note`,
    method: 'PUT',
    data: { note },
  });
};
